// ============================================================================
// StrengthSection — total tonnage headline + a ranked PR bar chart of best
// estimated 1RMs. Tonnage renders only when a set carried reps×weight; the PR
// chart only when there's at least one PR.
// ============================================================================
import { useTranslation } from "react-i18next";
import { Badge } from "../../../components";
import { BarChart } from "../charts/BarChart";
import { ReportSection } from "./ReportSection";
import type { TypedClientReport } from "../types";
import { fmtNum } from "../format";

export function StrengthSection({
  report,
  index,
}: {
  report: TypedClientReport;
  index: number;
}) {
  const { t, i18n } = useTranslation("clientReport");
  const locale = i18n.language;
  const { strength } = report;

  const prData = strength.prs.map((p) => ({
    label: p.exercise_name ?? t("strength.unnamed"),
    value: p.best_1rm,
  }));

  const topLift = strength.prs[0];

  return (
    <ReportSection
      id="report-strength"
      index={index}
      kicker={t("kicker")}
      title={t("strength.title")}
      subtitle={t("strength.subtitle")}
      hasData={strength.ok}
    >
      {strength.total_tonnage !== null && (
        <div className="mb-6 overflow-hidden rounded-lg bg-grad-violet p-5 text-white shadow-ember">
          <p className="text-xs font-bold uppercase tracking-kicker opacity-85">
            {t("strength.tonnage")}
          </p>
          <div className="mt-1 flex items-end gap-2">
            <span className="nums font-display text-4xl font-extrabold leading-none tracking-tightest sm:text-5xl">
              {fmtNum(strength.total_tonnage, 0, locale)}
            </span>
            <span className="mb-1 text-lg font-bold opacity-85">kg</span>
          </div>
          <p className="mt-2 max-w-md text-sm opacity-80">
            {t("strength.tonnageBlurb")}
          </p>
        </div>
      )}

      {prData.length > 0 && (
        <div>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="font-display text-sm font-bold text-body-soft">
              {t("strength.prs")}
            </h3>
            {topLift && (
              <Badge tone="ember" solid>
                {t("strength.topLift")}:{" "}
                {topLift.exercise_name ?? t("strength.unnamed")}
              </Badge>
            )}
          </div>
          <BarChart
            data={prData}
            accent="violet"
            decimals={1}
            unit=" kg"
            locale={locale}
          />
        </div>
      )}
    </ReportSection>
  );
}
